/* Primeros pasos — el onboarding de cuatro pasos (pantalla 6b del rediseño).
 *
 * Existe porque el producto NO FUNCIONA vacío: sin perfil la bandeja ordena por criterios generales
 * y la columna de afinidad sale en blanco. Quien entra por primera vez ve una lista que no le habla
 * y no tiene por qué adivinar que hay que ir a «Mi perfil» a cargar un RUP.
 *
 * TRES REGLAS QUE NO SE TOCAN:
 *
 * 1. «Saltar por ahora» SIEMPRE a la vista. Es la diferencia entre un onboarding y un peaje. Quien
 *    solo quiere mirar tiene derecho a mirar, y todo lo de aquí se puede hacer después en el perfil.
 * 2. Se dispara solo en una cuenta VACÍA —sin perfil y sin cartera— y una sola vez. A quien ya usa
 *    la aplicación no se le atraviesa nada, ni siquiera el primer día tras un despliegue.
 * 3. El paso 3 CONFIRMA lo que se leyó del RUP; no es un formulario vacío. Pedirle a alguien que
 *    escriba lo que su propio PDF acaba de decir es hacerle trabajo dos veces, y es la diferencia
 *    entre «leímos tu RUP» y «llena estos campos».
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { enviar, ErrorApi, obtener, subir } from '../api/cliente'
import { entero, normalizarObjeto, pesos, plazo } from '../api/formato'
import type {
  Meta, Perfil, RespuestaBusqueda, RupDestilado,
} from '../api/tipos'
import { PERFIL_VACIO, usePerfil } from '../estado/perfil'
import { IconoMas, IconoSi, IconoVolver } from '../ui/Iconos'
import { Banda } from '../ui/Banda'
import './Primeros.css'

const PASOS = ['Para quién', 'Su RUP', 'Dónde y cuánto', 'Listo']

export function Primeros() {
  const navegar = useNavigate()
  const { perfil, rup, editar, guardarYa, ponerRup } = usePerfil()
  const [paso, setPaso] = useState(0)
  const [varias, setVarias] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* Los departamentos salen de las FACETAS del cubo, no de una lista de los 33 escrita a mano: si
     un departamento no tiene procesos abiertos, ofrecerlo es ofrecer un filtro vacío. */
  const [depsDisponibles, setDepsDisponibles] = useState<string[]>([])
  useEffect(() => {
    obtener<Meta>('/api/oportunidades/meta')
      .then((m) => setDepsDisponibles(m.departamentos ?? []))
      .catch(() => setDepsDisponibles([]))
  }, [])

  /** Terminar y salir. Igual para «saltar» que para «listo»: lo que cambia es lo que se guardó. */
  const salir = useCallback(async (marcar: boolean) => {
    // `primeros_pasos` deja constancia de que ya se pasó por aquí. Va en el PERFIL —o sea, en la
    // cuenta— y no en el navegador: quien lo saltó en el portátil no tiene que volver a saltarlo
    // en la oficina.
    if (marcar) editar({ primeros_pasos: true })
    await guardarYa()
    navegar('/')
  }, [editar, guardarYa, navegar])

  return (
    <div className="primeros">
      <header className="primeros-barra">
        <span className="marca"><b>Vigía</b></span>
        <span className="primeros-nota">
          Lo puedes cambiar después en tu perfil ·{' '}
          {/* SIEMPRE visible, en los cuatro pasos. Es la diferencia entre un onboarding y un
              peaje: quien solo quiere mirar tiene derecho a mirar. */}
          <button type="button" className="primeros-saltar" onClick={() => void salir(true)}>
            saltar por ahora
          </button>
        </span>
      </header>

      <ol className="primeros-pasos" aria-label="Pasos">
        {PASOS.map((p, i) => (
          <li key={p} className={i === paso ? 'activo' : i < paso ? 'hecho' : ''}
              aria-current={i === paso ? 'step' : undefined}>
            <span className="primeros-n">{i < paso ? <IconoSi /> : i + 1}</span>
            <span>{p}</span>
          </li>
        ))}
      </ol>

      {error && <p className="primeros-error" role="alert">{error}</p>}

      <div className="primeros-hoja">
        {paso === 0 && <PasoQuien varias={varias} setVarias={setVarias}
                                  onSeguir={() => setPaso(1)} />}
        {paso === 1 && <PasoRup rup={rup} ponerRup={ponerRup} onError={setError}
                                onAtras={() => setPaso(0)} onSeguir={() => setPaso(2)} />}
        {paso === 2 && <PasoDonde perfil={perfil} rup={rup} editar={editar} deps={depsDisponibles}
                                  onAtras={() => setPaso(1)}
                                  onSeguir={async () => { await guardarYa(); setPaso(3) }} />}
        {paso === 3 && <PasoListo perfil={perfil} varias={varias} onTerminar={() => void salir(true)} />}
      </div>

      <p className="primeros-pie">Paso {paso + 1} de {PASOS.length}</p>
    </div>
  )
}

/* ── 1 · para quién ───────────────────────────────────────────────────────── */

function PasoQuien({ varias, setVarias, onSeguir }: {
  varias: boolean | null
  setVarias: (v: boolean) => void
  onSeguir: () => void
}) {
  return (
    <>
      <h1>¿Para quién vas a buscar procesos?</h1>
      <p className="primeros-bajada">
        Con esto sabemos si armamos un perfil o una cartera. Puedes agregar más empresas después,
        y quitarlas.
      </p>
      <div className="primeros-opciones">
        <button type="button" className={`opcion${varias === false ? ' elegida' : ''}`}
                aria-pressed={varias === false} onClick={() => setVarias(false)}>
          <b>Para mi empresa</b>
          <span>Soy quien decide o prepara las ofertas. Un solo perfil, con su RUP y sus
            territorios.</span>
        </button>
        <button type="button" className={`opcion${varias === true ? ' elegida' : ''}`}
                aria-pressed={varias === true} onClick={() => setVarias(true)}>
          <b>Para varias empresas</b>
          <span>Soy consultor o analista y llevo varias. Cada una con su perfil, su seguimiento y
            sus descartes; un solo listado para triarlas todas.</span>
        </button>
      </div>
      <div className="primeros-acciones">
        <button type="button" className="boton-primario" disabled={varias === null}
                onClick={onSeguir}>
          Continuar
        </button>
      </div>
      {/* Lo que la elección cambia se DICE, en vez de que se descubra después. Y no cambia nada
          irreversible: las dos rutas llevan al mismo sitio, solo cambia con qué se empieza. */}
      {varias === true && (
        <p className="primeros-aparte">
          Empezamos por la primera. Después, en «Tus empresas», añades las demás y aparece el modo
          «Todas» para triarlas juntas.
        </p>
      )}
    </>
  )
}

/* ── 2 · su RUP ───────────────────────────────────────────────────────────── */

function PasoRup({ rup, ponerRup, onError, onAtras, onSeguir }: {
  rup: RupDestilado | null
  ponerRup: (r: RupDestilado, sugerido?: Partial<Perfil>) => void
  onError: (e: string | null) => void
  onAtras: () => void
  onSeguir: () => void
}) {
  const [subiendo, setSubiendo] = useState(false)
  const [nit, setNit] = useState('')
  const [buscando, setBuscando] = useState(false)
  const archivo = useRef<HTMLInputElement>(null)

  const cargar = useCallback(async (f: File) => {
    setSubiendo(true)
    onError(null)
    try {
      const datos = new FormData()
      datos.append('archivo', f)
      const r = await subir<RupDestilado & { sectores?: string[] }>('/api/rup', datos)
      ponerRup({ ...r, fuente: 'rup' }, {
        ...(r.nit ? { nit: r.nit } : {}),
        ...(r.empresa ? { razon_social: r.empresa } : {}),
        ...(r.sectores?.length ? { sectores: r.sectores } : {}),
        ...(r.capacidad_max_pesos ? { max: Math.round(r.capacidad_max_pesos / 1e6) } : {}),
      })
      onSeguir()
    } catch (e) {
      onError(e instanceof ErrorApi ? e.message : String(e))
    } finally {
      setSubiendo(false)
    }
  }, [ponerRup, onError, onSeguir])

  const porNit = useCallback(async () => {
    const limpio = nit.replace(/\D/g, '')
    if (!limpio) return
    setBuscando(true)
    onError(null)
    try {
      const r = await obtener<RupDestilado & { ok: boolean; mensaje?: string }>(
        `/api/perfil-nit?nit=${encodeURIComponent(limpio)}`)
      if (!r.ok) {
        onError(r.mensaje ?? 'No encontré esa empresa en SECOP.')
        return
      }
      // `r.fuente` y no el literal: el servidor responde 'rues' para la empresa que aún no ha
      // contratado. Ver la nota gemela en `Perfil.tsx`.
      ponerRup({ ...r, fuente: r.fuente ?? 'secop' }, {
        nit: limpio,
        ...(r.empresa ? { razon_social: r.empresa } : {}),
        ...(r.sectores?.length ? { sectores: r.sectores } : {}),
        ...(r.perfil?.departamento ? { deps: [r.perfil.departamento] } : {}),
      })
      onSeguir()
    } catch (e) {
      onError(e instanceof ErrorApi ? e.message : String(e))
    } finally {
      setBuscando(false)
    }
  }, [nit, ponerRup, onError, onSeguir])

  return (
    <>
      <h1>Carga el RUP de la empresa</h1>
      <p className="primeros-bajada">
        Es el paso que más cambia el resultado: del PDF leemos las clases UNSPSC que declara, su
        capacidad residual y su domicilio, y con el NIT buscamos lo que ya ha ejecutado en SECOP.
      </p>

      <div className="primeros-rup">
        <div className="rup-soltar">
          <b>El RUP en PDF</b>
          <span>
            El certificado que expide la Cámara de Comercio. Se lee en unos segundos y no se
            comparte con nadie.
          </span>
          <input ref={archivo} type="file" accept="application/pdf" hidden
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void cargar(f) }} />
          <button type="button" className="boton-primario" disabled={subiendo}
                  onClick={() => archivo.current?.click()}>
            {subiendo ? 'Leyendo el PDF…' : 'Elegir archivo'}
          </button>
          <span className="rup-soltar-o">o búscala por NIT si no lo tienes a mano</span>
          <div className="rup-nit">
            <input inputMode="numeric" placeholder="NIT sin dígito de verificación"
                   value={nit} onChange={(e) => setNit(e.target.value)} />
            <button type="button" className="accion" disabled={buscando || !nit.trim()}
                    onClick={() => void porNit()}>
              {buscando ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
        </div>

        <div className="que-leemos">
          <span className="rotulo">Qué leemos del PDF</span>
          <ul>
            <li><IconoSi /> Clases UNSPSC declaradas</li>
            <li><IconoSi /> Capacidad residual y financiera</li>
            <li><IconoSi /> Domicilio y vigencia</li>
          </ul>
          {/* La salida sin RUP, dicha aquí y no escondida: es un paso que se puede pasar de largo
              y decirlo evita que alguien abandone buscando el PDF. */}
          <p className="que-leemos-nota">
            Sin RUP también funciona: se ordena por sector y territorio, con menos precisión.
          </p>
        </div>
      </div>

      <div className="primeros-acciones">
        <button type="button" className="accion" onClick={onAtras}><IconoVolver /> Atrás</button>
        <button type="button" className="accion" onClick={onSeguir}>
          {rup ? 'Continuar' : 'Seguir sin RUP'}
        </button>
      </div>
    </>
  )
}

/* ── 3 · dónde y cuánto ───────────────────────────────────────────────────── */

/**
 * CONFIRMA lo leído, no pide de nuevo lo que el PDF ya dijo.
 *
 * Si hubo RUP, la cabecera nombra lo que se sacó de él —cuántas clases, qué capacidad— y los
 * campos vienen rellenos con eso. Pedirle a alguien que escriba lo que su propio PDF acaba de
 * decir es hacerle el trabajo dos veces y desmiente el «leímos tu RUP» del paso anterior.
 */
function PasoDonde({ perfil, rup, editar, deps: disponibles, onAtras, onSeguir }: {
  perfil: Perfil
  rup: RupDestilado | null
  editar: (c: Partial<Perfil>) => void
  deps: string[]
  onAtras: () => void
  onSeguir: () => Promise<void>
}) {
  const deps = perfil.deps ?? []
  const alternar = (d: string) =>
    editar({ deps: deps.includes(d) ? deps.filter((x) => x !== d) : [...deps, d] })

  return (
    <>
      <h1>Dónde trabaja y cuánto puede ejecutar</h1>
      <p className="primeros-bajada">
        {rup?.fuente === 'rues' ? (
          <>
            Encontramos <b>{rup.empresa}</b> en el registro mercantil, sin contratos en SECOP.
            Puede seguir y navegar, pero todavía no calificamos el encaje: eso lo enciende el RUP.
          </>
        ) : rup ? (
          <>
            {rup.fuente === 'secop' ? 'Leímos su historial en SECOP: ' : 'Leímos su RUP: '}
            {rup.unspsc?.length ? <b>{entero(rup.unspsc.length)} clases UNSPSC</b> : 'sin clases'}
            {rup.capacidad_max_pesos
              ? <> y capacidad residual de <b>{pesos(rup.capacidad_max_pesos)}</b></> : null}
            . Confirma lo que falta y queda lista.
          </>
        ) : (
          <>Sin RUP hace falta lo básico: dónde trabaja y qué tamaño de contrato le sirve.</>
        )}
      </p>

      <div className="primeros-campos">
        <div>
          <span className="rotulo">Territorios donde trabaja</span>
          <div className="deps">
            {disponibles.map((d) => (
              <button type="button" key={d} aria-pressed={deps.includes(d)}
                      className={`dep${deps.includes(d) ? ' puesto' : ''}`}
                      onClick={() => alternar(d)}>
                {d}{deps.includes(d) && <IconoSi />}
              </button>
            ))}
          </div>
          {/* El territorio ORDENA, no esconde. Decirlo aquí evita que alguien marque medio país
              por miedo a perderse algo. */}
          <p className="primeros-ayuda">
            Verás procesos de todo el país; los de tus territorios suben en el orden.
          </p>
        </div>

        <div>
          <span className="rotulo">Tamaño de contrato que le sirve</span>
          <div className="tamanos">
            <label className="campo">
              <span className="rotulo">Mínimo</span>
              <input inputMode="numeric" value={perfil.min ?? ''}
                     onChange={(e) => editar({ min: e.target.value ? Number(e.target.value.replace(/\D/g, '')) : null })} />
              <span className="primeros-unidad">millones</span>
            </label>
            <label className="campo">
              <span className="rotulo">Máximo</span>
              <input inputMode="numeric" value={perfil.max ?? ''}
                     onChange={(e) => editar({ max: e.target.value ? Number(e.target.value.replace(/\D/g, '')) : null })} />
              <span className="primeros-unidad">millones</span>
            </label>
          </div>
          <p className="primeros-ayuda">
            {rup?.capacidad_max_pesos
              ? 'El máximo viene de la capacidad residual de su RUP; ajústalo si sabes que puede menos.'
              : 'En millones de pesos. Se puede dejar vacío y ajustarlo después.'}
          </p>
        </div>
      </div>

      <div className="primeros-acciones">
        <button type="button" className="accion" onClick={onAtras}><IconoVolver /> Atrás</button>
        <button type="button" className="boton-primario" onClick={() => void onSeguir()}>
          Guardar y ver mis oportunidades
        </button>
      </div>
    </>
  )
}

/* ── 4 · listo ────────────────────────────────────────────────────────────── */

/**
 * El cierre: cuántos le sirven HOY y los tres mejores CON su veredicto real.
 *
 * Se pide al servidor con el perfil recién guardado, así que las tres tarjetas son las mismas que
 * va a ver en la bandeja un segundo después. Enseñar tres ejemplos de utilería aquí sería la peor
 * primera impresión posible: la promesa se rompería en la pantalla siguiente.
 */
function PasoListo({ perfil, varias, onTerminar }: {
  perfil: Perfil
  varias: boolean | null
  onTerminar: () => void
}) {
  const [r, setR] = useState<RespuestaBusqueda | null>(null)
  const [fallo, setFallo] = useState(false)

  useEffect(() => {
    const c = new AbortController()
    enviar<RespuestaBusqueda>('/api/oportunidades/buscar', { limit: 3, orden: 'rel' }, c.signal)
      .then(setR)
      .catch(() => { if (!c.signal.aborted) setFallo(true) })
    return () => c.abort()
  }, [])

  const sirven = r?.conteo_bandas
    ? r.conteo_bandas.compite + r.conteo_bandas.cautela
    : null
  const nombre = perfil.razon_social?.trim()

  return (
    <>
      <span className="rotulo">Listo</span>
      {/* ⚠ TRES titulares y no uno. El diseño asume un perfil que encaja bien y escribe «hoy hay 68
          que te sirven»; con un perfil flojo esa frase sale «hoy hay 0 que te sirven» y debajo tres
          tarjetas, que es una contradicción en la pantalla de bienvenida. Cuando no hay ninguno en
          las bandas buenas se dice lo que de verdad pasa —el perfil todavía no distingue— y las
          tres tarjetas pasan a ser «las que más se acercan», que además es la información útil:
          hay algo que mejorar y se dice qué. */}
      <h1>
        {sirven === null ? 'Tu perfil quedó guardado'
          : sirven === 0
            ? <>Con este perfil todavía no destaca ninguno</>
            : <>Hoy hay <b>{entero(sirven)}</b> {sirven === 1 ? 'proceso' : 'procesos'} abiertos que{' '}
                {nombre ? `le sirven a ${nombre}` : 'te sirven'}</>}
      </h1>
      <p className="primeros-bajada">
        {sirven === 0 ? (
          <>
            Miramos los {entero(r?.total ?? null)} procesos abiertos que puedes ver y ninguno queda
            en «Compite». Suele significar que falta lo que más pesa: <b>el RUP o el NIT</b>, que es
            lo que deja comparar por lo que la empresa ya ejecutó y no por sectores generales.
            Abajo van los que más se acercan.
          </>
        ) : r?.total ? (
          <>De {entero(r.total)} abiertos que puedes ver. Los tres primeros ya traen su veredicto;
            los demás quedan ordenados por afinidad.</>
        ) : 'La lista queda ordenada por afinidad.'}
      </p>

      {fallo ? (
        <p className="primeros-ayuda">
          No pude traer la lista ahora mismo, pero tu perfil quedó guardado: entra a Oportunidades.
        </p>
      ) : (
        <ul className="primeros-tres">
          {(r?.procesos ?? []).map((p) => (
            <li key={p.id}>
              {p._banda && p._afinidad != null
                ? <Banda banda={p._banda} afinidad={p._afinidad} />
                : <span className="rotulo">sin afinidad</span>}
              <span className="primeros-tres-que">
                <b>{normalizarObjeto(p.descripcion || p.objeto)}</b>
                <span>{pesos(p.precio_base)} · {plazo(p.fecha_cierre).toLowerCase()}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* ⚠ LA PROMESA DEL RESUMEN, con su verdad. El diseño dice «te enviaremos un resumen diario»
          y el repositorio NO tiene ninguna infraestructura de correo: prometerlo aquí sería la
          primera promesa incumplida del producto, y la haría en la pantalla de bienvenida. Se dice
          qué se guarda y qué falta para que llegue. */}
      <p className="primeros-aviso">
        Puedes pedir un <b>resumen diario</b> con lo que pase cierto umbral de afinidad; la
        preferencia se guarda en tu perfil. <b>El envío por correo todavía no está conectado</b>, así
        que por ahora el resumen se ve entrando a Oportunidades.
      </p>

      <div className="primeros-acciones">
        <button type="button" className="boton-primario" onClick={onTerminar}>
          Ver mis oportunidades
        </button>
        {varias && (
          <span className="primeros-ayuda">
            <IconoMas /> Para añadir las demás empresas: «Tus empresas», arriba a la derecha.
          </span>
        )}
      </div>
    </>
  )
}

/** ¿Hay que enseñarle los primeros pasos a esta cuenta? */
export function necesitaPrimeros(perfil: Perfil, existe: boolean): boolean {
  // Ya pasó por aquí (los hizo o los saltó): nunca más.
  if (perfil.primeros_pasos) return false
  // Y una cuenta que YA tiene perfil no es una cuenta nueva, aunque nunca haya visto esta pantalla:
  // a quien ya usa la aplicación no se le atraviesa un onboarding el día del despliegue.
  return !existe && !(perfil.nit || perfil.sectores?.length || perfil.deps?.length)
}

export const _pruebasPrimeros = { necesitaPrimeros, PERFIL_VACIO }
